import React, {useCallback} from 'react';
import {NodeProps} from 'reactflow';

import BenchlingNode, {BenchlingNodeProps} from '../BenchlingNode/BenchlingNode';
import {NodeHandleProps, RowHandleProps, BenchlingNodeData} from '../BenchlingNode/BenchlingNodeTypes';
import {SimpleBenchlingNodeData, SimpleRowData} from './SimpleBenchlingNodeTypes';
import {SimpleBenchlingNodeHandle} from './sub-components/SimpleBenchlingNodeHandle';
import {SimpleBenchlingNodeSectionRow} from './sub-components/SimpleBenchlingNodeSectionRow';
import {SimpleBenchlingNodeSectionRowHandle} from './sub-components/SimpleBenchlingNodeSectionRowHandle';

type SimpleBenchlingNodeProps<TNodeData extends SimpleBenchlingNodeData> = NodeProps<TNodeData>;

/**
 * An implementation of the BenchlingNode that uses the SimpleBenchlingNodeSectionRow
 * for easy out-of-the-box functionality for rendering rows.
 *
 * Not sure whether to use SimpleBenchlingNode or BenchlingNode?
 * 1. Does SimpleRowData work for your use case? If no -> must use BenchlingNode, otherwise go to 2
 * 2. Do you want the default row renderer? If yes -> SimpleBenchlingNode, otherwise go to 3
 * 3. Do you want the default node handle renderer? If yes -> SimpleBenchlingNode, otherwise can use either component
 *
 * @example
 * // Basic usage with default row data
 * function MyComponent(props: {}) {
 *   const nodeData: SimpleBenchlingNodeData = {
 *     nodeName: "My Node",
 *     sections: [
 *       {
 *         sectionName: "Section 1",
 *         sectionRows: [{ id: "row1", primaryColumn: "Value" }]
 *       }
 *     ]
 *   };
 *   return <SimpleBenchlingNode data={nodeData} />;
 * }
 *
 * @example
 * // Usage with custom renderers
 * function MyComponent(props: {}) {
 *   const nodeData: SimpleBenchlingNodeData = {
 *     nodeName: "My Node",
 *     sections: [
 *       {
 *         sectionName: "Section 1",
 *         sectionRows: [{ id: "row1", primaryColumn: "Value" }]
 *       }
 *     ],
 *     rowRenderer: (rowData) => <div>{rowData.primaryColumn}</div>,
 *     // Only show a target handle for each row
 *     rowHandleRenderer: (props) => props.handleType === HandleType.TARGET ? <Handle /> : null,
 *     // Only show a source handle for the node header
 *     nodeHeaderHandleRenderer: (props) => props.handleType === HandleType.SOURCE ? <Handle /> : null,
 *   };
 *   return <SimpleBenchlingNode data={nodeData} />;
 * }
 *
 * @example
 * // Usage with custom handle ID generation
 * function MyComponent(props: {}) {
 *   const nodeData: SimpleBenchlingNodeData = {
 *     nodeName: "My Node",
 *     sections: [
 *       {
 *         sectionName: "Section 1",
 *         sectionRows: [{ id: "row1", primaryColumn: "Value" }]
 *       }
 *     ],
 *     // Customize the row handle ID generation
 *     rowHandleIdGenerator: (rowId, handleType) => `${rowId}-${handleType}`,
 *     // Customize the node header handle ID generation
 *     nodeHeaderHandleIdGenerator: (nodeId, handleType) => `${nodeId}-${handleType}`,
 *   };
 *   return <SimpleBenchlingNode data={nodeData} />;
 * }
 */
export default function SimpleBenchlingNode<TNodeData extends SimpleBenchlingNodeData>(
  props: SimpleBenchlingNodeProps<TNodeData>
): JSX.Element {
  const {rowCustomizations, nodeHandleCustomizations, ...nodeConfig} = props.data;
  const {nodeHeaderHandleRenderer, defaultNodeHeaderHandleOverrides} = nodeHandleCustomizations ?? {};
  const {rowRenderer, rowHandleRenderer, defaultRowHandleOverrides} = rowCustomizations ?? {};

  /** Allow developers to use the default handle with a custom ID generator */
  const buildDefaultNodeHeaderHandleRenderer = useCallback(
    (nodeHeaderHandleProps: NodeHandleProps): JSX.Element => (
      <SimpleBenchlingNodeHandle
        nodeId={nodeHeaderHandleProps.nodeId}
        handleType={nodeHeaderHandleProps.handleType}
        nodeHeaderHandleIdGenerator={defaultNodeHeaderHandleOverrides?.nodeHeaderHandleIdGenerator}
        additionalProps={
          defaultNodeHeaderHandleOverrides?.nodeHeaderHandleAdditionalProps?.(nodeHeaderHandleProps) ?? {}
        }
      />
    ),
    [defaultNodeHeaderHandleOverrides]
  );

  /** Allow developers to use the default handle with a custom ID generator */
  const buildDefaultRowHandleRenderer = useCallback(
    (rowHandleProps: RowHandleProps<SimpleRowData>): JSX.Element | null => (
      <SimpleBenchlingNodeSectionRowHandle
        rowData={rowHandleProps.rowData}
        handleType={rowHandleProps.handleType}
        rowHandleIdGenerator={defaultRowHandleOverrides?.rowHandleIdGenerator}
        additionalProps={defaultRowHandleOverrides?.rowHandleAdditionalProps?.(rowHandleProps) ?? {}}
      />
    ),
    [defaultRowHandleOverrides]
  );

  /** Allow developers to use the default row renderer with optionally customized row handle renderers */
  const buildDefaultRowRenderer = useCallback(
    (rowData: SimpleRowData): JSX.Element => (
      <SimpleBenchlingNodeSectionRow
        {...rowData}
        rowHandleRenderer={rowHandleRenderer ?? buildDefaultRowHandleRenderer}
      />
    ),
    [buildDefaultRowHandleRenderer, rowHandleRenderer]
  );

  const renderers = {
    rowRenderer: rowRenderer ?? buildDefaultRowRenderer,
    nodeHeaderHandleRenderer: nodeHeaderHandleRenderer ?? buildDefaultNodeHeaderHandleRenderer,
  };

  /** Convert SimpleBenchlingNode node data into BenchlingNode shapes */
  const propsWithDefaultRowRenderer: BenchlingNodeProps<SimpleRowData, BenchlingNodeData<SimpleRowData>> = {
    ...props,
    data: {
      ...nodeConfig,
      ...renderers,
    },
  };

  return <BenchlingNode<SimpleRowData, BenchlingNodeData<SimpleRowData>> {...propsWithDefaultRowRenderer} />;
}
